import { defineStore } from 'pinia'
import { apiClient } from '../api/client'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'

interface UserInfo {
  id: number
  username: string
  firstName: string
  lastName: string
  role: number
  roleType: string
  team: number | null
  email: string | null
}

interface UserPermission {
  user_id: number
  first_name: string
  last_name: string
  email: string
  team_id: number
  team_name: string
  role_id: number
  role_name: string
  is_admin: boolean
  is_coach: boolean
  is_player: boolean
}

const INVITE_TOKEN_KEY = 'pending_invite_token'
const ACTIVE_TEAM_KEY = 'active_team_id'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const token = ref(localStorage.getItem('token') || '')
  const isAuthenticated = ref(!!token.value)
  const userInfo = ref<UserInfo | null>(null)
  const userPermissions = ref<UserPermission[]>([])
  const isAdmin = ref(false)
  const storedActiveTeam = Number(localStorage.getItem(ACTIVE_TEAM_KEY))
  const activeTeamId = ref<number | null>(
    Number.isFinite(storedActiveTeam) && storedActiveTeam > 0 ? storedActiveTeam : null
  )

  const isCoach = computed(() => userPermissions.value.some(p => p.is_coach))

  // Player-only users land on My Stats; coaches and admins keep the dashboard.
  const homeRoute = computed(() =>
    isAdmin.value || isCoach.value ? '/dashboard' : '/my-stats'
  )

  const accessibleTeams = computed(() => {
    if (userPermissions.value.some(p => p.is_admin)) return []
    return [...new Set(userPermissions.value.map(p => p.team_id))]
  })

  // Deduped {team_id, team_name} pairs from the user's permissions, for the
  // header team switcher. Empty for admins (they load the full team list).
  const myTeams = computed(() => {
    const seen = new Map<number, string>()
    for (const p of userPermissions.value) {
      if (!p.is_admin && !seen.has(p.team_id)) seen.set(p.team_id, p.team_name)
    }
    return [...seen.entries()].map(([team_id, team_name]) => ({ team_id, team_name }))
  })

  const hasRole = (roleName: string, teamId?: number) => {
    if (userPermissions.value.some(p => p.is_admin)) return true
    return userPermissions.value.some(p => {
      const roleMatch = p.role_name === roleName
      const teamMatch = teamId ? p.team_id === teamId : true
      return roleMatch && teamMatch
    })
  }

  const canManageTeam = (teamId: number) =>
    hasRole('Administrator', teamId) || hasRole('Coach', teamId)

  const canViewTeam = (teamId: number) => {
    if (userPermissions.value.some(p => p.is_admin)) return true
    return userPermissions.value.some(p => p.team_id === teamId)
  }

  const switchTeam = (teamId: number) => {
    activeTeamId.value = teamId
    localStorage.setItem(ACTIVE_TEAM_KEY, String(teamId))
  }

  const refreshUserInfo = async () => {
    try {
      const response = await apiClient.get('/auth/user-info')
      userInfo.value = response.data.user
      userPermissions.value = response.data.permissions || []
      isAdmin.value = response.data.user.role === 1 ||
        response.data.permissions?.some((p: UserPermission) => p.is_admin) || false
      localStorage.setItem('userInfo', JSON.stringify(response.data.user))
      localStorage.setItem('userPermissions', JSON.stringify(response.data.permissions || []))

      // Auto-select the first accessible team when none is active, and heal
      // a stale stored id (team deleted or access lost). Admins have an empty
      // accessibleTeams list — the Header reconciles theirs against /teams.
      const teams = accessibleTeams.value
      if (teams.length > 0 && (!activeTeamId.value || !teams.includes(activeTeamId.value))) {
        switchTeam(teams[0])
      }

      return true
    } catch {
      return false
    }
  }

  // Stores the invite token and redirects to Google OAuth.
  // Called from InvitePage when the user clicks "Accept with Google".
  const handleInviteFlow = async (inviteToken: string) => {
    localStorage.setItem(INVITE_TOKEN_KEY, inviteToken)
    const response = await apiClient.get(`/auth/google?invite_token=${inviteToken}`)
    if (response.data.auth_url) {
      window.location.href = response.data.auth_url
    }
  }

  // Called from App.vue on the `/` route after Google redirects back with ?token=&user=
  const handleOAuthCallback = async (
    sessionToken: string,
    _username: string,
    opts: { skipRedirect?: boolean } = {}
  ) => {
    token.value = sessionToken
    isAuthenticated.value = true
    localStorage.setItem('token', sessionToken)
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${sessionToken}`

    // Clear any stored invite token — it was consumed server-side during callback
    localStorage.removeItem(INVITE_TOKEN_KEY)

    const success = await refreshUserInfo()
    if (!success) {
      logout()
      throw new Error('Failed to fetch user information')
    }

    if (!opts.skipRedirect) {
      await router.push(homeRoute.value)
    }
    return { success: true }
  }

  const logout = () => {
    token.value = ''
    isAuthenticated.value = false
    userInfo.value = null
    userPermissions.value = []
    isAdmin.value = false
    activeTeamId.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('userInfo')
    localStorage.removeItem('userPermissions')
    localStorage.removeItem(ACTIVE_TEAM_KEY)
    delete apiClient.defaults.headers.common['Authorization']
    router.push('/login')
  }

  const initializeFromStorage = () => {
    const storedUserInfo = localStorage.getItem('userInfo')
    const storedPermissions = localStorage.getItem('userPermissions')
    if (storedUserInfo) {
      try {
        const parsed = JSON.parse(storedUserInfo)
        userInfo.value = parsed
        if (storedPermissions) {
          const perms = JSON.parse(storedPermissions)
          userPermissions.value = perms
          isAdmin.value = parsed.role === 1 || perms.some((p: UserPermission) => p.is_admin)
        } else {
          isAdmin.value = parsed.role === 1
        }
      } catch {
        localStorage.removeItem('userInfo')
        localStorage.removeItem('userPermissions')
      }
    }
  }

  if (token.value) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
    initializeFromStorage()
  }

  return {
    token,
    isAuthenticated,
    userInfo,
    userPermissions,
    isAdmin,
    isCoach,
    homeRoute,
    activeTeamId,
    accessibleTeams,
    myTeams,
    hasRole,
    canManageTeam,
    canViewTeam,
    switchTeam,
    refreshUserInfo,
    handleInviteFlow,
    handleOAuthCallback,
    logout,
  }
})
